import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { Zap, FileText, Send, Calendar, Check, ArrowRight } from "react-feather";
import { Navbar } from "./Navbar";

const api = process.env.REACT_APP_API_URL;

/* The pricing page. Every paid offering in one place, with live prices from
   the backend and the payment rules stated plainly. No subscriptions and no
   hidden charges; that fact is the best sales copy we have. */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const dollars = (cents, fallback) =>
  cents != null ? `$${(cents / 100).toFixed(2)}` : fallback;

function Prices({ embedded = false }) {
  const navigate = useNavigate();
  const signedIn = !!Cookies.get("access_token");
  const [cfg, setCfg] = useState(null);   // {standard, express, credit_pack} in cents

  useEffect(() => {
    let alive = true;
    fetch(`${api}payment-config/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (alive && c) setCfg(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const go = (path, needsAccount) => {
    if (needsAccount && !signedIn) navigate("/signin", { state: { next: path } });
    else navigate(path);
  };

  const PLANS = [
    {
      icon: Zap,
      name: "Lightning Fast",
      price: dollars(cfg?.express, "$0.99"),
      unit: "one time",
      tagline: "In a hurry before a deadline",
      features: [
        "No account needed",
        "Upload, pay once, download in minutes",
        "Keyword-optimized ATS resume",
        "Polished PDF",
      ],
      cta: "Start now",
      onClick: () => navigate("/express"),
      accent: "amber",
    },
    {
      icon: FileText,
      name: "Standard Resume",
      price: dollars(cfg?.standard, "$1.99"),
      unit: "per resume",
      tagline: "The full resume workshop",
      popular: true,
      features: [
        "Resume score against any job description",
        "Matched and missing keyword analysis",
        "AI tailoring from your real experience",
        "Four professional templates, PDF download",
        "Rescore after tailoring, same honest method",
      ],
      cta: "Build my resume",
      onClick: () => go("/upload", true),
      accent: "blue",
    },
    {
      icon: Send,
      name: "Auto-Apply Credits",
      price: dollars(cfg?.credit_pack, "$24.99"),
      unit: "per 1,000 applications",
      tagline: "The job search on autopilot",
      features: [
        "Live US job feed, ranked to your resume",
        "One click fills the employer's real form",
        "You review and press Submit yourself",
        "Tracker with employer confirmations",
        "A credit is spent only when an application is sent",
        "Failed runs refund the credit automatically",
      ],
      cta: "Open Auto-Apply",
      onClick: () => go("/auto-apply", true),
      accent: "sky",
    },
    {
      icon: Calendar,
      name: "Career Coaching",
      price: "Priced at booking",
      unit: "per session",
      tagline: "A real advisor, one on one",
      features: [
        "Resume review with a person",
        "Interview practice",
        "Search strategy for your situation",
        "Booked on a real calendar",
      ],
      cta: "Book a session",
      onClick: () => navigate("/appointment"),
      accent: "indigo",
    },
  ];

  const ACCENTS = {
    amber: "from-amber-500 to-orange-500",
    blue: "from-blue-600 to-blue-500",
    sky: "from-sky-500 to-blue-600",
    indigo: "from-indigo-500 to-blue-600",
  };

  return (
    <>
      {!embedded && <Navbar />}
      <div className="bg-white">

        {/* Header */}
        <div className="bg-gradient-to-b from-sky-50 via-blue-50/40 to-white">
          <div className="max-w-5xl mx-auto px-6 pt-14 pb-12 text-center">
            <motion.h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              Simple pricing, no subscriptions
            </motion.h1>
            <motion.p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              Pay for what you use, once. No monthly plan, no auto-renewal,
              and nothing charged unless something real happened.
            </motion.p>
          </div>
        </div>

        {/* The four offerings */}
        <div className="max-w-7xl mx-auto px-6 pb-4">
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {PLANS.map(({ icon: Icon, name, price, unit, tagline, features, cta, onClick, accent, popular }, i) => (
              <motion.div
                key={name}
                className={`relative flex flex-col bg-white rounded-2xl p-6 border transition hover:shadow-xl hover:shadow-blue-600/5 ${
                  popular ? "border-blue-400 shadow-lg shadow-blue-600/10" : "border-gray-200"}`}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                {popular && (
                  <span className="absolute -top-3 left-6 text-[11px] font-bold text-white bg-blue-600 px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <span className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${ACCENTS[accent]} text-white items-center justify-center shadow-md`}>
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-gray-900">{name}</h3>
                <p className="text-xs text-gray-500">{tagline}</p>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-3xl font-extrabold text-gray-900">{price}</span>
                  <span className="text-xs text-gray-400">{unit}</span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onClick}
                  className={`mt-5 inline-flex items-center justify-center gap-2 font-bold px-4 py-2.5 rounded-xl transition ${
                    popular
                      ? "bg-blue-600 text-white hover:bg-blue-500 shadow-md shadow-blue-600/20"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                >
                  {cta} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* The payment rules, stated plainly */}
        <div className="max-w-5xl mx-auto px-6 py-12">
          <motion.div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-8" {...fadeUp}>
            <h2 className="font-bold text-gray-900">How billing works here</h2>
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600">
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Every payment is one-time. There is no subscription to cancel because there is no subscription.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Payments are processed by Stripe. We never see or store your card number.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Auto-Apply charges a credit only when an application is actually sent. Failures refund themselves.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Full terms are in our Terms of Service, written in plain language.</li>
            </ul>
          </motion.div>
        </div>

        {/* Close (standalone page only; the landing has its own closer) */}
        {!embedded && (
        <div className="max-w-5xl mx-auto px-6 pb-16">
          <motion.div className="bg-gradient-to-r from-blue-600 to-sky-500 rounded-3xl px-8 py-10 text-center text-white shadow-xl shadow-blue-600/20" {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Less than a coffee to fix your resume.
            </h2>
            <p className="mt-2 text-blue-100 text-sm">And about a penny per application after that.</p>
            <button
              onClick={() => go("/upload", true)}
              className="mt-6 inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-7 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              Start with my resume <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
        )}
      </div>
    </>
  );
}

export default Prices;
